import { Controller } from "@hotwired/stimulus"
import { get } from "@rails/request.js"
import { escapeHTML, mountGlModalShell } from "../lib/gl_modal_shell"
import { describeInputBy } from "../lib/described_by"

// Forced-choice confirmation gate for an IMO/Agency text input (org entry gate,
// Phase 4). Wired on the same wrapper as autocomplete-input/org-suggestion.
// Where org-suggestion is an unobtrusive nudge, this is the hard stop: when the
// agent commits (blurs/tabs off) a name that's a near-miss of an existing org,
// or that's actually a known agency typed into the IMO field
// (misclassification), a forced-choice gl-modal opens. The agent must pick an
// existing match, apply the structural correction, or explicitly confirm
// "I'm creating a new one" — dismissing the modal (Esc/backdrop/×) is NOT
// acceptance, so the typed value is cleared rather than silently kept, since
// the server-side gate (Organizations::ImoAgencyResolver) would reject it
// unconfirmed anyway.
//
// Wire on a wrapper element:
//   data-controller="org-confirm-gate"
//   data-org-confirm-gate-url-value / -organization-type-value ("imo"|"agency")
//   data-org-confirm-gate-parent-id-value (optional, agency scoping — kept in
//   sync with imo-agency's syncAgencySuggesterParent)
// with:
//   - the text input: 'org-confirm-gate-target': 'input', and actions
//     "input->org-confirm-gate#resetConfirmed change->org-confirm-gate#commit"
//   - a hidden field: 'org-confirm-gate-target': 'confirmedInput'
// No target is needed for the lookup-failure message: the controller appends
// its own `gl-alert` to the wrapper while a lookup is refused (#12801).
export default class extends Controller {
  static targets = ["input", "confirmedInput"]
  static values = {
    url: String,
    organizationType: String,
    parentId: String,
    minChars: { type: Number, default: 3 },
  }

  connect() {
    this.modalOpen = false
    this.pendingCommit = false
    this.recommitQueued = false
    this.shell = null
    this.lookupError = null
    this.openFromServerPayload()
  }

  // Server-driven open (re-render path): when a submit was refused by the
  // server-side gate, the re-rendered wrapper carries the suggestions as a
  // data attribute — open the forced-choice modal immediately so the refusal
  // is the same experience as the commit-time check it raced past. The
  // attribute is consumed (removed) so a later Turbo reconnect can't replay it.
  openFromServerPayload() {
    const raw = this.element.dataset.orgConfirmGateServerPayload
    if (!raw) return

    delete this.element.dataset.orgConfirmGateServerPayload
    try {
      this.openFromPayload(JSON.parse(raw))
    } catch {
      // Malformed payload — fail open; the inline error message still shows.
    }
  }

  // Server-driven open (JSON path): the dashboard confirmation modal dispatches
  // org-confirm-gate:open on this wrapper with the 422 body's gate payload.
  openFromEvent(event) {
    this.openFromPayload(event.detail || {})
  }

  openFromPayload(payload) {
    if (this.modalOpen) return

    const matches = payload.matches || []
    const misclassification = payload.misclassification || null
    if (matches.length === 0 && !misclassification) return

    this.openModal({ matches, misclassification })
  }

  disconnect() {
    this.teardownModal()
    this.clearLookupError()
  }

  // Bound to the field's `input` action (every keystroke). A name the agent
  // already confirmed as "new" needs re-confirmation the moment it changes —
  // and a lookup failure painted for the previous value no longer describes
  // the field.
  resetConfirmed() {
    if (this.hasConfirmedInputTarget) this.confirmedInputTarget.value = ""
    this.clearLookupError()
  }

  // Bound to the field's `change` action — fires when the name is committed
  // (blur/select), not on every keystroke.
  async commit() {
    // Re-entrancy guards. `modalOpen` covers "a gate modal is already open";
    // `pendingCommit` covers the lookup window BEFORE the modal exists — two
    // `change` events can land back-to-back (an autocomplete that dispatches
    // `change` on selection followed by the native blur `change`), and without
    // a synchronous flag both would pass the guard, both would fetch, and two
    // modals would stack for the same field.
    if (this.modalOpen) return
    if (this.pendingCommit) {
      // Remember that a commit was dropped. Two changes for the SAME value still
      // produce one lookup and one modal; a change for a DIFFERENT value (an
      // inline "Use it", a parent-IMO fix, more typing then Tab) means the value
      // now in the field still owes its own check once this lookup settles —
      // see `#settle`. Without this, the guard would silently leave that value
      // unchecked and only the server's 422 re-render could catch it.
      this.recommitQueued = true
      return
    }

    const name = this.inputTarget.value.trim()
    if (name.length < this.minCharsValue) return

    this.pendingCommit = true
    this.recommitQueued = false
    // The field is being checked: assistive tech hears it, and the rendered spec
    // waits on it before asserting that no gate followed a suggestion click.
    this.inputTarget.setAttribute("aria-busy", "true")
    let recheck = false
    try {
      const data = await this.lookup(name)
      if (data) recheck = this.settle(name, data)
    } finally {
      this.pendingCommit = false
      this.inputTarget.removeAttribute("aria-busy")
    }
    if (recheck) await this.commit()
  }

  // One lookup for `name`. Resolves to the JSON body, or to null when the server
  // refused it or the network dropped it — after painting the inline error
  // (#12801). The SUBMIT still fails open on purpose: the server-side gate
  // (Organizations::ImoAgencyResolver) is the backstop, and a client that
  // blocked Save on a lookup outage would strand the agent instead. What must
  // never be silent is the failure itself — a refused lookup with nothing on
  // screen leaves the agent looping on an unexplained 422 (help desk #2447).
  async lookup(name) {
    const query = { name, organization_type: this.organizationTypeValue }
    if (this.hasParentIdValue && this.parentIdValue) query.parent_id = this.parentIdValue

    try {
      const response = await get(this.urlValue, { query, responseKind: "json" })
      if (!response.ok) {
        this.showLookupError()
        return null
      }
      const data = await response.json
      this.clearLookupError()
      return data
    } catch {
      this.showLookupError()
      return null
    }
  }

  // Judge a settled lookup for `name` against the value the field holds NOW.
  // Returns whether the current value still owes a check of its own.
  //
  // The mousedown on an inline suggestion ("Use it", a parent-IMO choice) blurs
  // the field, so its `change` commits the STALE near-miss before the click
  // rewrites the value; the click's own `change` is dropped by the re-entrancy
  // guard. The typo's lookup would then resolve into a gate offering the very
  // name the click had just filled in (#12948). So: an answer for a value no longer
  // in the field is dropped. If the field now holds one of the names the server
  // just returned, the agent accepted an existing organization and there is
  // nothing to confirm. Otherwise the moved value is re-checked exactly once,
  // and only if its own `change` was dropped — a value still being typed waits
  // for its blur like any other.
  settle(name, data) {
    const matches = (data && data.matches) || []
    const misclassification = (data && data.misclassification) || null
    const current = this.inputTarget.value.trim()

    if (current !== name) {
      if (nameAmong(current, matches)) return false
      return this.recommitQueued
    }

    // Live guard: the server's `exact` flag is authoritative for "the typed
    // value already IS an existing org" — SimilarOrgSuggester's `matches`
    // deliberately EXCLUDES exact (case-insensitive) name matches by
    // construction, so a name picked straight from the autocomplete (e.g.
    // "Family First" when "Family First Life" also exists) can arrive here
    // with `exact: true` and a non-empty `matches` array. Trust `exact`, not a
    // client-side re-derivation from `matches`.
    if (data && data.exact) return false

    // Nothing to confirm.
    if (matches.length === 0 && !misclassification) return false
    // Redundant now that `exact` (above) is authoritative — kept as a harmless
    // belt-and-suspenders check in case a future response shape omits `exact`.
    if (nameAmong(name, matches)) return false

    this.openModal({ matches, misclassification })
    return false
  }

  // The lookup failed: one visible, assertive message under the field — the
  // failure blocks the agent's progress, so `role="alert"` is right (FORM-2 /
  // A11Y-3), it holds prose only (an assertive region never wraps a control),
  // and it is never a browser-native dialog (MODAL-3). Painted once, described
  // to the input, cleared by the next edit or the next lookup that succeeds.
  showLookupError() {
    if (this.lookupError) return

    const alert = document.createElement("div")
    alert.className = "gl-alert gl-alert--danger gl-alert--compact mt-2"
    alert.setAttribute("role", "alert")
    alert.id = `org-confirm-gate-lookup-error-${(lookupErrorSeq += 1)}`

    const icon = document.createElement("i")
    icon.className = "ph-fill ph-warning-circle"
    icon.setAttribute("aria-hidden", "true")

    const content = document.createElement("div")
    content.className = "gl-alert-content small"
    content.textContent = LOOKUP_ERROR_MESSAGE

    alert.append(icon, content)
    this.element.appendChild(alert)
    this.lookupError = alert
    // Held by reference: clearLookupError() also runs from disconnect(), after
    // Stimulus has torn down the target scope and `this.inputTarget` would throw.
    this.lookupErrorInput = this.inputTarget
    describeInputBy(this.lookupErrorInput, alert.id, true)
  }

  clearLookupError() {
    if (!this.lookupError) return

    describeInputBy(this.lookupErrorInput, this.lookupError.id, false)
    this.lookupError.remove()
    this.lookupError = null
    this.lookupErrorInput = null
  }

  openModal({ matches, misclassification }) {
    this.modalOpen = true
    this.optionChosen = false

    const organizationLabel = displayLabel(this.organizationTypeValue)
    const createNewLabel = `Create New ${properLabel(this.organizationTypeValue)}`

    // Shell lifecycle (markup skeleton, stacking z-index, Bootstrap instance,
    // teardown, backdrop bump) belongs to lib/gl_modal_shell.js — only the
    // forced-choice option list is this controller's business.
    //
    // The footer carries exactly ONE action — the explicit "none of these, I'm
    // creating a new one" confirmation — so under MODAL-1 it is the dialog's
    // primary (a lone footer button is always gl-btn-primary), a direct footer
    // child with no width override (MODAL-2), and a short label that always fits
    // at 390px (BUTTON-5). The full decision stays readable: the body sentence
    // names the button and what choosing it means, and the button's accessible
    // name starts with its visible label and carries the "none of these" clause.
    // Match / Join choices live in the body, not the footer. (PE-032 / R-15,
    // campaign #12564 — the previous flat, full-width utility button.)
    this.shell = mountGlModalShell({
      idPrefix: "org-confirm-gate-modal",
      title: `Confirm your ${organizationLabel}`,
      headerIcon: "ph-fill ph-question",
      headerClass: "gl-modal-header--dark",
      bodyHTML: `
              <p class="gl-modal-text mb-3">We found a possible match. Pick one below, or choose <strong>${escapeHTML(createNewLabel)}</strong> to confirm none of these is yours and you're creating a new ${escapeHTML(organizationLabel)}.</p>
              <div class="org-confirm-gate-options"></div>`,
      buttons: [
        {
          text: createNewLabel,
          className: "gl-btn gl-btn-primary",
          onClick: () => this.chooseCreateNew(),
        },
      ],
      onMounted: (shell) => {
        // Accessible name = visible label + the clause the short label drops
        // (WCAG 2.5.3 label-in-name: the visible text leads the name).
        const createNewButton = shell.footer.querySelector("button")
        createNewButton.setAttribute("aria-label", `${createNewLabel} — none of these matches is my ${organizationLabel}`)

        const optionsEl = shell.element.querySelector(".org-confirm-gate-options")

        // Misclassification (structural signal) comes first, then fuzzy matches.
        const agencies = (misclassification && misclassification.agencies) || []
        agencies.forEach((agency) => {
          const row = optionRow("Join", `Join ${agency.agency_name} under ${agency.imo_name} (${agency.agents_count} agents)`)
          row.identity.append(
            strongNode(agency.agency_name),
            document.createTextNode(" under "),
            strongNode(agency.imo_name),
            document.createTextNode(` (${agency.agents_count} agents)`)
          )
          shell.on(row.button, "click", () => this.chooseAgency(agency))
          optionsEl.appendChild(row.element)
        })

        matches.forEach((match) => {
          const row = optionRow("Use", `Use ${match.name}`)
          row.identity.append(strongNode(match.name))
          shell.on(row.button, "click", () => this.chooseMatch(match.name))
          optionsEl.appendChild(row.element)
        })
      },
      // Stacked above a host modal (the dashboard confirmation), Bootstrap's
      // FocusTrap.activate() focuses the new dialog BEFORE it removes the host's
      // trap listeners, so the host trap sees focus leave and parks it on its own
      // first control (the host's × button); only then does the gate's trap take
      // over. `shown.bs.modal` fires after that handoff, so a focus here lands in
      // the gate and stays (A11Y-2: a dialog keeps keyboard focus). On the base
      // this was masked — the stale gate of #12948 had already deactivated every
      // trap on the page when it closed. The shell owning stacked focus for every
      // dynamic dialog is the better home; recorded as a follow-up.
      onShown: (shell) => {
        if (!shell.element.contains(document.activeElement)) shell.element.focus()
      },
      onHidden: () => {
        const wasChosen = this.optionChosen
        this.teardownModal()
        if (!wasChosen) {
          // Forced choice — dismissal (Esc/backdrop/×) is NOT acceptance.
          this.inputTarget.value = ""
          this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
        }
      },
    })
  }

  chooseMatch(name) {
    this.optionChosen = true
    this.inputTarget.value = name
    this.inputTarget.dispatchEvent(new Event("input", { bubbles: true }))
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }))
    this.shell.hide()
  }

  // The typed name is actually a known agency — dispatch a bubbling event so
  // the host's imo-agency controller (already wired to the identical
  // org-suggestion:fix-misclassification event) can set both the IMO and
  // agency fields. This controller does not touch its own input for this
  // choice; the correction always targets the IMO+agency pair together.
  chooseAgency(agency) {
    this.optionChosen = true
    this.dispatch("apply-agency", { detail: { imoName: agency.imo_name, agencyName: agency.agency_name } })
    this.shell.hide()
  }

  chooseCreateNew() {
    this.optionChosen = true
    if (this.hasConfirmedInputTarget) this.confirmedInputTarget.value = "true"
    this.shell.hide()
  }

  // Safe to call from either the shell's own `hidden.bs.modal` path or
  // disconnect() — the shell's destroy() is idempotent.
  teardownModal() {
    if (this.shell) this.shell.destroy()
    this.shell = null
    this.modalOpen = false
  }
}

// Copy for a lookup the server refused or the network dropped (#12801). One
// message for every failure: the agent's next move is the same whatever the
// status — try the name again — and the server-side gate still decides Save.
const LOOKUP_ERROR_MESSAGE = "We couldn't check this name against existing organizations — please try again."

// Distinct ids for the lookup-error alerts, so each input's aria-describedby
// can name its own (two gated fields — IMO and agency — share every consumer).
let lookupErrorSeq = 0

function nameAmong(name, matches) {
  const wanted = name.toLowerCase()
  return matches.some((match) => match.name.toLowerCase() === wanted)
}

// One body choice: the organization's identity as wrapping TEXT (names are
// arbitrary length and arrive as text nodes, never markup) beside a concise
// one-line Secondary button whose accessible name carries the whole sentence.
// Button Text Fit Contract: a button label always fits on one line as complete
// words, so the name is never the label — a long IMO name, or an ordinary
// "Join X under Y (N agents)" at 390px, would clip inside a full-width
// button. Layout is Bootstrap flex utilities only; no per-screen stylesheet.
function optionRow(label, accessibleName) {
  const element = document.createElement("div")
  element.className = "d-flex align-items-center justify-content-between gap-2 mb-2"

  const identity = document.createElement("div")
  identity.className = "gl-modal-text text-break flex-grow-1 mb-0"

  const button = document.createElement("button")
  button.type = "button"
  button.className = "gl-btn gl-btn-outline gl-btn-sm flex-shrink-0"
  button.textContent = label
  button.setAttribute("aria-label", accessibleName)

  element.append(identity, button)
  return { element, identity, button }
}

// organizationTypeValue is the wire value ("imo"|"agency"), lowercase because
// that's what the resolver/suggester key off of. "imo" is an acronym and must
// render as "IMO" in user-facing copy, not lowercased; "agency" is an ordinary
// word and renders as typed. Falls back to "organization" for an unwired type.
function displayLabel(organizationType) {
  if (organizationType === "imo") return "IMO"
  return organizationType || "organization"
}

// Proper Case for a button label ("Create New Agency"), where the running-text
// form above stays lowercase ("Confirm your agency"). The acronym is unchanged.
function properLabel(organizationType) {
  const label = displayLabel(organizationType)
  return label === "IMO" ? label : label.charAt(0).toUpperCase() + label.slice(1)
}

function strongNode(text) {
  const strong = document.createElement("strong")
  strong.textContent = text
  return strong
}
